import React, { useEffect, useState } from 'react';

import { Paper, Stack, Title, Text, Group, Divider } from '@mantine/core';
import useAttendanceRules from 'hooks/attendanceRule/useAttendanceRules';
import { AthletePackageRetrieve, AttendanceRuleRetrieve } from 'types';
import { getAttendanceRuleSummary, getEndConditions } from 'utils/billing.helpers';

import CreditDispensationTable from './components/CreditDispensationTable';

interface ICreditsTabProps {
  athletePackage: AthletePackageRetrieve;
  permission?: boolean;
}

const CreditsTab = ({ athletePackage, permission = true }: ICreditsTabProps): JSX.Element => {
  const { data, isSuccess } = useAttendanceRules({
    packageId: athletePackage.package.packageId,
  });
  const applicableAttendanceRuleIds: string[] = Array.from(
    new Set(
      athletePackage.creditDispensations
        .map(cd => cd.attendanceRule)
        .filter((attendanceRuleId): attendanceRuleId is string => Boolean(attendanceRuleId)),
    ),
  );
  const [applicableAttendanceRules, setApplicableAttendanceRules] = useState<AttendanceRuleRetrieve[]>([]);

  useEffect(() => {
    if (data?.data && isSuccess) {
      setApplicableAttendanceRules(
        data.data.filter(ar => applicableAttendanceRuleIds.includes(ar.attendanceRuleId)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data, isSuccess, athletePackage]);

  const endConditions = getEndConditions(applicableAttendanceRules, athletePackage.package.type);

  return (
    <Stack>
      <Group position='apart'>
        <Title order={3}>Credit Dispensation Summary</Title>
      </Group>
      <Paper shadow='sm' radius='md' withBorder>
        <Stack spacing='xs' py='sm'>
          <Group grow noWrap px='md' ta='center'>
            <Text c='dimmed' fs='italic' size='sm' fw='bold'>
              Event Type
            </Text>
            <Text c='dimmed' fs='italic' size='sm' fw='bold'>
              Attendance
            </Text>
            <Text c='dimmed' fs='italic' size='sm' fw='bold'>
              Validity
            </Text>
            <Text c='dimmed' fs='italic' size='sm' fw='bold'>
              Delay Start
            </Text>
          </Group>
          <Divider />
          {getAttendanceRuleSummary(applicableAttendanceRules).map(ar => (
            <Group key={ar.key} grow noWrap px='md' ta='center'>
              <Text size='sm'>{ar.name}</Text>
              <Text size='sm'>{ar.attendance}</Text>
              <Text size='sm'>{ar.validity}</Text>
              <Text size='sm'>{ar.delay}</Text>
            </Group>
          ))}
          {endConditions && (
            <>
              <Divider />
              <Text size='sm' px='md' ta='center' data-cy='endConditionsCreditsTab'>
                {endConditions}
              </Text>
            </>
          )}
        </Stack>
      </Paper>
      <Title order={3} mt='lg'>
        Dispensations
      </Title>
      <CreditDispensationTable
        athletePackage={athletePackage}
        attendanceRules={applicableAttendanceRules}
        permission={permission}
      />
    </Stack>
  );
};

export default CreditsTab;
